import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { AdminButton, cx } from './AdminUI';

const AdminLayout = () => {
  const { admin, logout } = useAdminAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isAffiliateMenuOpen, setIsAffiliateMenuOpen] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith('/admin/affiliate-managment')) {
      setIsAffiliateMenuOpen(true);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  const navClass = ({ isActive }) =>
    cx(
      'block rounded-xl px-3 py-2 text-sm font-medium transition',
      isActive
        ? 'bg-slate-900 text-white dark:bg-cyan-500 dark:text-slate-950'
        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
    );

  return (
    <div className="min-h-screen bg-slate-100/70 dark:bg-slate-950">
      <div className="mx-auto flex min-h-screen w-full max-w-[1700px] flex-col lg:flex-row">
        <aside className="w-full border-b border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 lg:w-80 lg:border-b-0 lg:border-r lg:p-6">
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 p-4 text-white shadow-lg shadow-cyan-500/25">
            <p className="text-xs uppercase tracking-[0.2em] text-white/80">Control Center</p>
            <h2 className="mt-1 text-2xl font-semibold">Admin System</h2>
          </div>
          <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
          {admin?.username || admin?.email}
          </p>
          <nav className="space-y-2">
            <NavLink to="/admin/dashboard" className={navClass}>
              Dashboard
            </NavLink>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              onClick={() => setIsAffiliateMenuOpen((prev) => !prev)}
            >
              <span>Affiliate Management</span>
              <span aria-hidden="true">{isAffiliateMenuOpen ? '▾' : '▸'}</span>
            </button>
            {isAffiliateMenuOpen && (
              <div className="ml-2 space-y-1 border-l border-slate-200 pl-3 dark:border-slate-700">
                <NavLink
                  to="/admin/affiliate-managment/existing-affiliates"
                  className={navClass}
                >
                  Existing Affiliates
                </NavLink>
                <NavLink
                  to="/admin/affiliate-managment/new-requests"
                  className={navClass}
                >
                  New Requests
                </NavLink>
              </div>
            )}
            <NavLink to="/admin/user-managment" className={navClass}>
              User Management
            </NavLink>
            <NavLink to="/admin/promo-codes" className={navClass}>
              Promo Codes
            </NavLink>
            <NavLink to="/admin/sale-campaigns" className={navClass}>
              Sale Campaigns
            </NavLink>
          </nav>
          <div className="mt-6">
            <AdminButton onClick={toggleTheme} variant="ghost" className="mb-2 w-full">
              {isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            </AdminButton>
            <AdminButton onClick={handleLogout} variant="secondary" className="w-full">
              Logout
            </AdminButton>
          </div>
        </aside>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
